import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { api, connectSocket, getSlug } from "./lib";

const vibrate = (ms=30) => (navigator.vibrate?.(ms));
const Confirm = (msg) => window.confirm(msg);
const promptPin = () => window.prompt("Enter 4-digit PIN to modify:", "");

const PERIODS = ["Q1","Q2","Q3","Q4","OT","OT1","OT2","OT3","OT4"];
const DOWNS   = ["1st","2nd","3rd","4th"];
const YTG_OPTS = ["N/A","GOAL", ...Array.from({length:99},(_,i)=>String(i+1))];

function formatMMSS(s=0){
  const t = Math.max(0, Math.floor(s));
  const mm = String(Math.floor(t/60)).padStart(2,"0");
  const ss = String(t%60).padStart(2,"0");
  return `${mm}:${ss}`;
}

// 2-letter abbreviation (first letters of up to first two words)
const abbr2 = (name="") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const a = (parts[0]?.[0] || "").toUpperCase();
  const b = (parts[1]?.[0] || "").toUpperCase();
  return (a + b || "??");
};

function Controller() {
  const slugFromURL = getSlug();
  const [registered, setRegistered] = useState(false);
  const [reg, setReg] = useState({ gameSlug: slugFromURL, firstName:"", lastName:"", email:"", phone:"" });
  const [pin] = useState(localStorage.getItem("ADMIN_PIN") || "8484");
  const [game, setGame] = useState(null);
  const [busy, setBusy] = useState(false);

  const slug = useMemo(() => (reg.gameSlug || slugFromURL), [reg.gameSlug, slugFromURL]);

  useEffect(() => {
    if (!registered || !slug) return;
    api(`/api/games/${slug}`).then(setGame).catch(()=>{});
    const s = connectSocket({ slug });
    s.on("state", setGame);
    return () => s.disconnect();
  }, [registered, slug]);

  async function doRegister(e){
    e.preventDefault();
    vibrate();
    const { gameSlug, firstName, lastName, email, phone } = reg;
    if (!gameSlug || !firstName || !lastName || !email || !phone) { alert("Please fill out all fields."); return; }
    setBusy(true);
    try {
      await api("/api/register", { method:"POST", body:{ gameSlug, firstName, lastName, email, phone }});
      setRegistered(true);
      history.replaceState(null, "", `?slug=${encodeURIComponent(gameSlug)}`);
      api(`/api/games/${gameSlug}`).then(setGame).catch(()=>{});
    } catch(err){ alert(err.message || "Registration failed"); }
    finally { setBusy(false); }
  }

  async function doPatch(body){
    vibrate();
    if (game?.locked) { alert('Game is locked. Use "Modify Game" to unlock with PIN.'); return; }
    setBusy(true);
    try { await api(`/api/games/${slug}`, { method:"PATCH", body, pin }); }
    catch(e){ alert(e.message); }
    finally { setBusy(false); }
  }

  async function setScore(side, delta){
    const curr = game?.[side]?.score || 0;
    await doPatch({ [side]: { score: curr + delta }});
  }

  // Touchdown: +6, set possession, emit tdAt/tdSide (for overlay),
  // and temporarily rename team to "TOUCHDOWN" for 8 seconds (then revert).
  async function td2(side){
    const inc = side === "home"
      ? { home: { score: (game?.home?.score||0) + 6 } }
      : { away: { score: (game?.away?.score||0) + 6 } };

    const prevName = game?.[side]?.name || (side === "home" ? "Home" : "Away");

    // Clear touchdown (falling edge)
    await doPatch({ banners: { touchdown: false } });
    await new Promise(r => setTimeout(r, 50));

    // Raise touchdown and set possession
    await doPatch({
      ...inc,
      possession: side,
      banners: { tdAt: Date.now(), tdSide: side }
    });

    // Temporarily rename
    await doPatch({ [side]: { name: "TOUCHDOWN" } });
    setTimeout(async () => {
      // Only revert if still showing placeholder (avoid clobbering user edits)
      const fresh = await api(`/api/games/${slug}`);
      const currName = fresh?.[side]?.name;
      if (currName === "TOUCHDOWN") {
        await doPatch({ [side]: { name: prevName } });
      }
    }, 8000);
  }

  async function setNames(home, away){ await doPatch({ home:{ name: home }, away:{ name: away }}); }
  async function setPossession(v){ await doPatch({ possession: v || null }); }
  async function setPeriod(v){ await doPatch({ period: v }); }
  async function setDowns(v){ await doPatch({ downs: v || null }); }

  // Map: N/A -> null, GOAL -> 0 (numeric), else 1..99 number
  async function setYTG(v){
    let val = null;
    if (v === "GOAL") val = 0;
    else if (v !== "N/A") val = Math.max(1, Math.min(99, parseInt(v,10) || 1));
    await doPatch({ yardsToGo: val });
  }

  async function setAllotted(side, n){
    await doPatch({ [side]: { timeoutsAllotted: Math.max(0, parseInt(n||"3",10)) }});
  }
  async function useTimeout(side, undo=false){
    vibrate(15);
    await api(`/api/games/${slug}/timeout`, { method:"POST", body:{ side, undo }, pin });
  }

  // Clock API
  async function clkStart(){ await api(`/api/games/${slug}/clock/start`, { method:"POST", pin }); }
  async function clkStop(){ await api(`/api/games/${slug}/clock/stop`,  { method:"POST", pin }); }
  async function clkLen(sec){ await api(`/api/games/${slug}/clock/set-length`, { method:"POST", body:{ seconds: sec }, pin }); }
  async function clkAdj(sec){ await api(`/api/games/${slug}/clock/adjust`,     { method:"POST", body:{ delta:   sec }, pin }); }

  // Mutually exclusive banner toggles
  async function toggleBanner(which){
    const next = !(game?.banners?.[which]);
    const payload = { banners: { flag:false, timeout:false, halftime:false }};
    payload.banners[which] = next;
    await doPatch(payload);
  }

  async function submitFinal(){
    if (!Confirm("Submit FINAL score? This will lock the controller.")) return;
    await api(`/api/games/${slug}/final`, { method:"POST", pin });
  }
  async function abandonGame(){
    if (!Confirm("ABANDON game? This will lock the controller.")) return;
    await api(`/api/games/${slug}/abandon`, { method:"POST", pin });
  }
  async function modifyGame(){
    const p = promptPin();
    if (p !== pin) { alert("Incorrect PIN"); return; }
    await api(`/api/games/${slug}/unlock`, { method:"POST", pin });
    api(`/api/games/${slug}`).then(setGame).catch(()=>{});
  }

  if (!registered) {
    return (
      <div className="wrap">
        <h2>Register to Control a Game</h2>
        <form onSubmit={doRegister} className="card" style={{maxWidth:700}}>
          <div className="row"><div className="label">Game ID</div>
            <input className="grow" value={reg.gameSlug} onChange={e=>setReg(v=>({...v, gameSlug:e.target.value}))} placeholder="e.g. demo"/></div>
          <div className="row"><div className="label">First Name</div>
            <input className="grow" value={reg.firstName} onChange={e=>setReg(v=>({...v, firstName:e.target.value}))}/></div>
          <div className="row"><div className="label">Last Name</div>
            <input className="grow" value={reg.lastName} onChange={e=>setReg(v=>({...v, lastName:e.target.value}))}/></div>
          <div className="row"><div className="label">Email</div>
            <input className="grow" type="email" value={reg.email} onChange={e=>setReg(v=>({...v, email:e.target.value}))}/></div>
          <div className="row"><div className="label">Phone</div>
            <input className="grow" value={reg.phone} onChange={e=>setReg(v=>({...v, phone:e.target.value}))} placeholder="555-555-5555"/></div>
          <div className="row"><button className="btn ok" disabled={busy} type="submit">Register</button></div>
        </form>
      </div>
    );
  }

  if (!game) return <div className="wrap">Loading…</div>;

  const home   = game.home || {};
  const away   = game.away || {};
  const clock  = game.clock || {};
  const locked = !!game.locked;

  const possOpts = [
    { v:"",     label:"Possession: None" },
    { v:"home", label:`Poss. ${abbr2(home.name || "Home")}` },
    { v:"away", label:`Poss. ${abbr2(away.name || "Away")}` },
  ];

  const btnStartStyle = { background:"#1f7a2e", borderColor:"#2c8f3d", color:"#fff" };
  const btnStopStyle  = { background:"#b3261e", borderColor:"#c03a32", color:"#fff" };
  const isOn = (k) => !!game?.banners?.[k];

  return (
    <div className="wrap">
      <div className="header">
        <div className="big">Game: {slug}</div>
        {!locked ? (
          <div style={{display:"flex", gap:8}}>
            <button className="btn ok"   onClick={submitFinal}  disabled={busy}>Submit Final Score</button>
            <button className="btn warn" onClick={abandonGame} disabled={busy}>Abandon Game</button>
          </div>
        ) : (
          <button className="btn" onClick={modifyGame}>Modify Game</button>
        )}
      </div>

      <div className="grid2">
        {/* Column 1 */}
        <div className="card">
          <div className="title">Teams &amp; Scores</div>

          <div className="row">
            <input className="grow" placeholder="Home Team" defaultValue={home.name||""}
                   onBlur={e=>setNames(e.target.value, away.name||"")} disabled={locked}/>
            <div className="big">{home.score ?? 0}</div>
            <button className="btn" onClick={()=>setScore("home", +1)} disabled={locked}>+1</button>
            <button className="btn" onClick={()=>setScore("home", -1)} disabled={locked}>-1</button>
          </div>

          <div className="row">
            <button className="btn grow" onClick={()=>td2("home")} disabled={locked}>
              {home.name||"Home"} Touchdown +6
            </button>
          </div>

          <div className="row">
            <input className="grow" placeholder="Away Team" defaultValue={away.name||""}
                   onBlur={e=>setNames(home.name||"", e.target.value)} disabled={locked}/>
            <div className="big">{away.score ?? 0}</div>
            <button className="btn" onClick={()=>setScore("away", +1)} disabled={locked}>+1</button>
            <button className="btn" onClick={()=>setScore("away", -1)} disabled={locked}>-1</button>
          </div>

          <div className="row">
            <button className="btn grow" onClick={()=>td2("away")} disabled={locked}>
              {away.name||"Away"} Touchdown +6
            </button>
          </div>

          <div className="row">
            <select value={game.possession||""} onChange={e=>setPossession(e.target.value||null)} disabled={locked}>
              {possOpts.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>

            <select value={game.period||"Q1"} onChange={e=>setPeriod(e.target.value)} disabled={locked}>
              {PERIODS.map(p=><option key={p} value={p}>{p}</option>)}
            </select>

            <select value={game.downs||""} onChange={e=>setDowns(e.target.value||null)} disabled={locked}>
              <option value="">Downs: —</option>
              {DOWNS.map(d=><option key={d} value={d}>{d}</option>)}
            </select>

            <select value={game.yardsToGo==null ? "N/A" : (game.yardsToGo===0 ? "GOAL" : String(game.yardsToGo))}
                    onChange={e=>setYTG(e.target.value)} disabled={locked}>
              {YTG_OPTS.map(val => (
                <option key={val} value={val}>{val === "N/A" ? "Yards To Go: N/A" : (val === "GOAL" ? "Yards: GOAL" : `Yards: ${val}`)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Column 2 */}
        <div className="card">
          <div className="title">Clock</div>

          <div className="row" style={{display:"flex", alignItems:"center", gap:8}}>
            <div className="big">{formatMMSS(clock.seconds||0)}</div>
            {clock.running
              ? <button className="btn" style={{...btnStopStyle, flex:1}}  onClick={clkStop}  disabled={locked}>Stop</button>
              : <button className="btn" style={{...btnStartStyle, flex:1}} onClick={clkStart} disabled={locked}>Start</button>
            }
          </div>

          <div className="row" style={{display:"flex", alignItems:"center", gap:8}}>
            <select id="perLen" defaultValue={String(clock.periodLengthSeconds||720)} disabled={locked} style={{flex:1}}>
              <option value="900">15:00</option>
              <option value="720">12:00</option>
              <option value="600">10:00</option>
            </select>
            <button className="btn" onClick={()=>{
              const s = parseInt(document.getElementById("perLen").value,10);
              clkLen(s);
            }} disabled={locked}>Set</button>
          </div>

          <div className="row" style={{display:"flex", alignItems:"center", gap:8}}>
            <div className="grow">{home.name||"Home"}: Used {home.timeoutsUsed||0} / Remain {Math.max(0,(home.timeoutsAllotted||3)-(home.timeoutsUsed||0))}</div>
            <input type="number" min="0" defaultValue={home.timeoutsAllotted||3}
                   onBlur={e=>setAllotted("home", e.target.value)} disabled={locked} style={{width:80}}/>
            <button className="btn" onClick={()=>useTimeout("home", false)} disabled={locked}>Use Timeout</button>
            <button className="btn" onClick={()=>useTimeout("home", true)}  disabled={locked}>Undo</button>
          </div>

          <div className="row" style={{display:"flex", alignItems:"center", gap:8}}>
            <div className="grow">{away.name||"Away"}: Used {away.timeoutsUsed||0} / Remain {Math.max(0,(away.timeoutsAllotted||3)-(away.timeoutsUsed||0))}</div>
            <input type="number" min="0" defaultValue={away.timeoutsAllotted||3}
                   onBlur={e=>setAllotted("away", e.target.value)} disabled={locked} style={{width:80}}/>
            <button className="btn" onClick={()=>useTimeout("away", false)} disabled={locked}>Use Timeout</button>
            <button className="btn" onClick={()=>useTimeout("away", true)}  disabled={locked}>Undo</button>
          </div>

          <div className="row" style={{display:"flex", gap:8, marginTop:8}}>
            <button className={`btn grow ${isOn("flag") ? "ok" : ""}`}     onClick={()=>toggleBanner("flag")}     disabled={locked}>Flag</button>
            <button className={`btn grow ${isOn("timeout") ? "ok" : ""}`}  onClick={()=>toggleBanner("timeout")}  disabled={locked}>Timeout</button>
            <button className={`btn grow ${isOn("halftime") ? "ok" : ""}`} onClick={()=>toggleBanner("halftime")} disabled={locked}>Halftime</button>
          </div>
        </div>
      </div>
    </div>
  );
}

createRoot(document.getElementById("root")).render(<Controller />);
